import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { omit } from 'lodash'

import { Button, ButtonType } from '@/components/atoms/button'
import { RoomPropType } from '@/components/organisms/customer/_guest-room/not-edit-form-item'
import { PaxFullObject } from '@/models/guest-room/guest-room-assign'
import { Popup } from '@/components/atoms/popup'

type PaxModalProps = {
  isOpen: boolean
  onClose: () => void
  room: RoomPropType
  roomIndex: number
  onEditPaxFull: (value: PaxFullObject, index: number) => void
}

export const PaxModal: React.FC<PaxModalProps> = ({ isOpen, onClose, room, roomIndex, onEditPaxFull }) => {
  const { t } = useTranslation()
  const [localValues, setLocalValues] = useState<PaxFullObject>({})

  const hasChange = useMemo(
    () =>
      Number(room.paxChildA) !== Number(localValues.paxChildA) ||
      Number(room.paxChildB) !== Number(localValues.paxChildB) ||
      Number(room.paxChildC) !== Number(localValues.paxChildC) ||
      Number(room.paxChildD) !== Number(localValues.paxChildD) ||
      Number(room.paxChildE) !== Number(localValues.paxChildE) ||
      Number(room.paxChildF) !== Number(localValues.paxChildF) ||
      Number(room.paxChildOther) !== Number(localValues.paxChildOther) ||
      Number(room.paxFemale) !== Number(localValues.paxFemale) ||
      Number(room.paxMale) !== Number(localValues.paxMale),
    [room, localValues],
  )

  const onChangePax = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const value = parseInt(event.target.value) || 0

    setLocalValues({
      ...localValues,
      [type]: value,
    })
  }

  const onClickConfirm = () => {
    onEditPaxFull(
      {
        ...localValues,
        pax: Object.keys(omit(localValues, ['pax'])).reduce((sum, key) => Number(sum) + Number(localValues[key]), 0),
      },
      roomIndex,
    )
    onClose()
  }

  useEffect(() => {
    setLocalValues({
      paxChildA: room.paxChildA ? Number(room.paxChildA).toString() : '0',
      paxChildB: room.paxChildB ? Number(room.paxChildB).toString() : '0',
      paxChildC: room.paxChildC ? Number(room.paxChildC).toString() : '0',
      paxChildD: room.paxChildD ? Number(room.paxChildD).toString() : '0',
      paxChildE: room.paxChildE ? Number(room.paxChildE).toString() : '0',
      paxChildF: room.paxChildF ? Number(room.paxChildF).toString() : '0',
      paxChildOther: room.paxChildOther ? Number(room.paxChildOther).toString() : '0',
      paxFemale: room.paxFemale ? Number(room.paxFemale).toString() : '0',
      paxMale: room.paxMale ? Number(room.paxMale).toString() : '0',
      pax: room.pax ? Number(room.pax).toString() : '0',
    })
  }, [room])

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => {
        if (document.activeElement?.tagName !== 'INPUT') {
          onClose()
        }
      }}
      customStyle={paxModal}
    >
      <div css={fieldsContainerForAdult}>
        <div css={fieldContainer}>
          <div className="title">{t('Male')}</div>
          <input
            value={localValues.paxMale ? Number(localValues.paxMale).toString() : '0'}
            onChange={event => onChangePax(event, 'paxMale')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer}>
          <div className="title">{t('Female')}</div>
          <input
            value={localValues.paxFemale ? Number(localValues.paxFemale).toString() : '0'}
            onChange={event => onChangePax(event, 'paxFemale')}
            type="number"
            min={0}
          />
        </div>
      </div>
      <div css={fieldsContainerForChildren}>
        <div css={fieldContainer}>
          <div className="title">A</div>
          <input
            value={localValues.paxChildA ? Number(localValues.paxChildA).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildA')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer}>
          <div className="title">B</div>
          <input
            value={localValues.paxChildB ? Number(localValues.paxChildB).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildB')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer}>
          <div className="title">C</div>
          <input
            value={localValues.paxChildC ? Number(localValues.paxChildC).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildC')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer}>
          <div className="title">D</div>
          <input
            value={localValues.paxChildD ? Number(localValues.paxChildD).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildD')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer}>
          <div className="title">E</div>
          <input
            value={localValues.paxChildE ? Number(localValues.paxChildE).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildE')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer}>
          <div className="title">F</div>
          <input
            value={localValues.paxChildF ? Number(localValues.paxChildF).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildF')}
            type="number"
            min={0}
          />
        </div>
        <div css={fieldContainer} style={{ width: 100 }}>
          <div className="title">{t('Others')}</div>
          <input
            value={localValues.paxChildOther ? Number(localValues.paxChildOther).toString() : '0'}
            onChange={event => onChangePax(event, 'paxChildOther')}
            type="number"
            min={0}
          />
        </div>
      </div>
      <div css={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <Button width={110} height={38} buttonType={ButtonType.Secondary} onClick={onClose} marginRight={16}>
          {t('Cancel')}
        </Button>
        <Button
          width={110}
          height={38}
          buttonType={hasChange ? ButtonType.Primary : ButtonType.PrimaryDisabled}
          isDisabled={!hasChange}
          onClick={onClickConfirm}
        >
          {t('Save')}
        </Button>
      </div>
    </Popup>
  )
}

const paxModal = css({
  maxWidth: 390,
  width: '100%',
  padding: '16px 28px',
  display: 'block',
})

const fieldsContainerForAdult = css({
  display: 'flex',
  alignItems: 'center',
  gap: '12px 24px',
})
const fieldsContainerForChildren = css({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: `repeat(auto-fill, calc(25% - ${(24 * 3) / 4}px))`,
  gap: '12px 24px',
  marginTop: 12,
})

const fieldContainer = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '.title': {
    fontSize: 12,
    color: '#676767',
    fontWeight: 'bold',
  },
  input: {
    width: 42,
    height: 32,
    color: '#272727',
    padding: '0 10px',
    borderRadius: 17,
    appearance: 'none',
    border: '1px solid #CCC',
  },
})
